import { Id, toast } from "react-toastify";

import {
  AiNodeContext,
  DuplicateProductType,
  ExperienceDto,
  ExperienceListDto,
  GenericExperienceFe,
  RedirectDto,
  ScheduleDto,
  ScratchCardOfferType,
  ShopifyJobType,
  VariationFe,
  WebsocketEventProgress,
  WidgetCategory,
  WidgetType,
} from "@intelligems/ig-types";
import { OfferPageType } from "@intelligems/ig-types/fe-only/offers";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ShareSettings } from "@src/features/analytics/types";
import { RootState } from "@src/store";

type WebsocketToast = Record<
  string, // experiment id
  Record<
    ShopifyJobType,
    {
      toast?: Id;
      progress: number;
    }
  >
>;

export interface CheckoutScriptDialogProps {
  code: string;
  isTest: boolean;
}

interface DialogProps {
  setDomain: any;
  imageLibrary: {
    renderLocation?: string;
    onUpdateImage?: (imageUrl: string) => void;
    renderIndex?: number;
  };
  billingStatus: any;
  freeTrial: any;
  largeDevicePreview: any;
  completePricingIntegration: any;
  upgradeToProfitOptimization: any;
  offersMarketingDialog: any;
  warningBase: any;
  manageSubscriptions: {
    action: "pause" | "cancel";
    stripeSubscriptionLinkId: string | null;
  };
  addModifications: any;
  onsiteEditsSave: any;
  onsiteEditRefresh: any;
  redirectToPluginDialog:
    | {
        id: string;
        experience: ExperienceDto;
        path?: string;
      }
    | undefined;
  rollouts:
    | {
        variationId?: string;
        filterTargeting?: any;
        tableTargeting?: any;
        testTargeting?: any;
        experimentGoals?: any;
        priceIds: string[];
        action: "upgrade" | "downgrade";
        stripeSubscriptionLinkId: string;
      }
    | undefined;
  waitForShopify: any;
  modifyStripeSubscription: any;
  createExperiment:
    | {
        hideBackButton?: boolean;
        showContentSection?: boolean;
        filterKeys: string[];
      }
    | undefined;
  setDuplicateProductStatus: any;
  shippingProgressBar: any;
  shippingRates: any;
  activeSchedule: {
    schedules: ScheduleDto[];
    experience: ExperienceListDto | undefined;
  };
  priceUpdater: { action: "pause" | "end" | "start" | "none" };
  priceUpdaterStart: any;
  marketPriceUpdater: { action: "pause" | "end" | "start" | "none" };
  marketPriceUpdaterStart: any;
  marketUpdateConfirm: {
    action: "pause" | "end" | "start" | "none";
    testGroupId?: string;
  };
  switchAudienceType: {
    newTab: number;
  };
  switchExpressionType: {
    newTab: number;
    queryParamIndex: number;
  };
  share: any;
  checkoutScript: CheckoutScriptDialogProps | undefined;
  priceTestCurrency: any;
  campaignTestCreateNew: any;
  scriptTag: any;
  duplicateProductErrors: DuplicateProductType | undefined;
  bulkSyncDuplicates: any;
  downloadPriceCsv: any;
  devicePreviewPath: any;
  updatedDetected: any;
  missingShopifyScope:
    | {
        initial?: boolean;
      }
    | undefined;
  confirmExperimentDeletion: any;
  confirmActiveExperimentSave: any;
  chooseContentTestType: any;
  scoped:
    | {
        title: string;
        message: string;
        yesButtonText: string;
        yesButtonAction: any;
        noButtonText: string;
        noButtonAction: any;
        showButtons: boolean;
      }
    | undefined;
  priceTemplateDownload: any;
  autoFillProductPrices: any;
  scheduleTest:
    | {
        experience: GenericExperienceFe;
      }
    | undefined;
  schedulePersonalization:
    | {
        experience: GenericExperienceFe;
        schedules: ScheduleDto[];
      }
    | undefined;
  onboarding: { noClose?: boolean; hideBackdrop?: boolean } | undefined;
  productTableWarning: any;
  deleteDiscountTier: any;
  dashboardFilters: any;
  analyticsFilters: any;
  editTestGroups: any;
  testSave: any;
  startTest: { title: string; text: string } | undefined;
  duplicateProductNotice: any;
  endNonPriceTest: { defaultToRollout: boolean } | undefined;
  previewTestGroupCode:
    | {
        id: string;
        path?: string;
        themeId?: string;
      }
    | undefined;
  previewExperiment:
    | {
        id: string;
        path?: string;
        themeId?: string;
      }
    | undefined;
  dataExport: any;
  groupWinners: any;
  analyticsViewType: any;
  rateBuilder: any;
  noRateSelect: any;
  createShippingError: any;
  startShippingError: any;
  thirdPartyCarrierWarning: any;
  adminOverrideStartExp: any;
  discountApplicationTypeSwitch: any;
  showUserInfoPopup: any;
  discountGwpVariant: {
    productId: string | null;
    variationId: string;
    tierIndex: number;
  };
  duplicateProducts: { showStartTestRow: boolean };
  redirectAssetQuickView:
    | {
        variation: VariationFe;
        redirectState: RedirectDto;
        screenshotPreviewPath: string;
        previewUrl: string;
      }
    | undefined;
  templateSnippetGate: any;
  discardTestChanges: any;
  discardConfigureAnalyticsChanges: any;
  insufficientPlan: any;
  upgradeToActivate: any;
  createPersonalization?: { dontNavigate?: boolean };
  confirmIgChecklist: { isAdminOverride?: boolean } | undefined;
  offerLimitReached: any;
  ineligibleProductsSelected: any;
  archivePersonalization: any;
  unarchivePersonalization: any;
  copyPersonalization: any;
  relatedExperienceActive: any;
  relatedPersonalizationActive: any;
  archiveCustomEvent: any;
  unarchiveCustomEvent: any;
  saveWidgetConfirm: any;
  removeArchivedKeyMetric: any;
  offers:
    | {
        isEdit?: boolean;
        isCreate?: boolean;
        targetPage?: OfferPageType;
      }
    | undefined;
  addOfferModifications: any;
  deleteComponent:
    | {
        componentType: WidgetCategory;
        variationId: string;
        widgetType?: WidgetType;
      }
    | undefined;
  removeComponent: any;
  quantityButtonsFullPageEditor:
    | {
        widgetId?: string;
        variationId: string;
        widgetType: "quantityButtons";
      }
    | undefined;
  messageBoxFullPageEditor:
    | {
        widgetId?: string;
        variationId: string;
        widgetType: "messageBoxPopup" | "messageBoxSlideOut";
      }
    | undefined;
  offerProgressBarFullPageEditor:
    | {
        widgetId?: string;
        variationId: string;
        widgetType: "discountProgressBar";
      }
    | undefined;
  shippingProgressBarFullPageEditor:
    | {
        widgetId?: string;
        variationId: string;
        widgetType: "shippingProgressBar";
      }
    | undefined;
}

type AiChat = {
  open: boolean;
  start: boolean;
  workflowId?: string;
  initialContext: any;
  sessionId?: string;
};

export interface IGlobalState {
  confetti: boolean;
  progresses: WebsocketToast;
  ignoreUpdateDetected: boolean;
  aiChat: AiChat;
  bypassUnsavedChanges: boolean;
  bottomAlert: boolean;
  dialogs: {
    [K in keyof DialogProps]: {
      isOpen: boolean;
      props: DialogProps[K];
    };
  };
  timings: {
    initialPath: string;
    timings: { [key: string]: number };
  };
  shareSettings: ShareSettings;
}

export const initialScopedProps = {
  title: "Oops! Something went wrong!",
  message: "Please contact support@intelligems.io for assistance.",
  yesButtonText: "Contact Support",
  yesButtonAction: "yes",
  noButtonAction: "howdy",
  noButtonText: "Close",
  showButtons: true,
};

const initialGlobalState: IGlobalState = {
  confetti: false,
  progresses: {},
  bottomAlert: false,
  ignoreUpdateDetected: false,
  aiChat: {
    open: false,
    start: false,
    workflowId: undefined,
    initialContext: undefined,
    sessionId: undefined,
  },
  bypassUnsavedChanges: false,
  dialogs: {
    imageLibrary: {
      isOpen: false,
      props: { renderLocation: undefined, onUpdateImage: undefined },
    },
    manageSubscriptions: {
      isOpen: false,
      props: {
        stripeSubscriptionLinkId: null,
        action: "pause",
      },
    },
    warningBase: { isOpen: false, props: null },
    addModifications: { isOpen: false, props: null },
    setDomain: { isOpen: false, props: null },
    offersMarketingDialog: { isOpen: false, props: null },
    rollouts: { isOpen: false, props: undefined },
    campaignTestCreateNew: { isOpen: false, props: null },
    redirectToPluginDialog: { isOpen: false, props: undefined },
    billingStatus: { isOpen: false, props: undefined },
    freeTrial: { isOpen: false, props: { isPersonalization: false } },
    onsiteEditsSave: { isOpen: false, props: undefined },
    updatedDetected: { isOpen: false, props: undefined },
    createExperiment: { isOpen: false, props: undefined },
    devicePreviewPath: { isOpen: false, props: undefined },
    largeDevicePreview: { isOpen: false, props: undefined },
    waitForShopify: { isOpen: false, props: undefined },
    scriptTag: { isOpen: false, props: undefined },
    completePricingIntegration: { isOpen: false, props: undefined },
    upgradeToProfitOptimization: { isOpen: false, props: undefined },
    modifyStripeSubscription: { isOpen: false, props: undefined },
    missingShopifyScope: { isOpen: false, props: undefined },
    setDuplicateProductStatus: { isOpen: false, props: undefined },
    share: { isOpen: false, props: undefined },
    duplicateProductNotice: { isOpen: false, props: { title: "", text: "" } },
    shippingProgressBar: { isOpen: false, props: undefined },
    chooseContentTestType: { isOpen: false, props: undefined },
    confirmActiveExperimentSave: { isOpen: false, props: undefined },
    upgradeToActivate: { isOpen: false, props: undefined },
    onsiteEditRefresh: { isOpen: false, props: undefined },
    shippingRates: { isOpen: false, props: undefined },
    activeSchedule: {
      isOpen: false,
      props: { schedules: [], experience: undefined },
    },
    priceUpdater: { isOpen: false, props: { action: "none" } },
    priceUpdaterStart: { isOpen: false, props: undefined },
    marketPriceUpdater: { isOpen: false, props: { action: "none" } },
    marketPriceUpdaterStart: { isOpen: false, props: undefined },
    marketUpdateConfirm: { isOpen: false, props: { action: "none" } },
    downloadPriceCsv: { isOpen: false, props: undefined },
    checkoutScript: { isOpen: false, props: undefined },
    priceTestCurrency: { isOpen: false, props: undefined },
    confirmExperimentDeletion: { isOpen: false, props: undefined },
    duplicateProductErrors: { isOpen: false, props: undefined },
    bulkSyncDuplicates: { isOpen: false, props: undefined },
    scoped: {
      isOpen: false,
      props: initialScopedProps,
    },
    priceTemplateDownload: { isOpen: false, props: undefined },
    onboarding: { isOpen: false, props: undefined },
    autoFillProductPrices: { isOpen: false, props: undefined },
    productTableWarning: { isOpen: false, props: undefined },
    deleteDiscountTier: { isOpen: false, props: undefined },
    dashboardFilters: { isOpen: false, props: undefined },
    analyticsFilters: { isOpen: false, props: undefined },
    editTestGroups: { isOpen: false, props: undefined },
    testSave: { isOpen: false, props: undefined },
    startTest: { isOpen: false, props: undefined },
    endNonPriceTest: { isOpen: false, props: undefined },
    previewExperiment: { isOpen: false, props: undefined },
    previewTestGroupCode: { isOpen: false, props: undefined },
    dataExport: { isOpen: false, props: undefined },
    groupWinners: { isOpen: false, props: undefined },
    duplicateProducts: {
      isOpen: false,
      props: {
        showStartTestRow: false,
      },
    },
    scheduleTest: { isOpen: false, props: undefined },
    schedulePersonalization: { isOpen: false, props: undefined },
    analyticsViewType: { isOpen: false, props: undefined },
    rateBuilder: { isOpen: false, props: undefined },
    noRateSelect: { isOpen: false, props: undefined },
    createShippingError: { isOpen: false, props: undefined },
    startShippingError: { isOpen: false, props: undefined },
    thirdPartyCarrierWarning: { isOpen: false, props: undefined },
    adminOverrideStartExp: { isOpen: false, props: undefined },
    discountApplicationTypeSwitch: { isOpen: false, props: undefined },
    showUserInfoPopup: { isOpen: false, props: undefined },
    discountGwpVariant: {
      isOpen: false,
      props: { productId: null, variationId: "", tierIndex: 0 },
    },
    redirectAssetQuickView: { isOpen: false, props: undefined },
    templateSnippetGate: { isOpen: false, props: undefined },
    discardTestChanges: { isOpen: false, props: undefined },
    discardConfigureAnalyticsChanges: { isOpen: false, props: undefined },
    insufficientPlan: { isOpen: false, props: undefined },
    createPersonalization: { isOpen: false, props: undefined },
    confirmIgChecklist: { isOpen: false, props: undefined },
    switchAudienceType: {
      isOpen: false,
      props: {
        newTab: 0,
      },
    },
    switchExpressionType: {
      isOpen: false,
      props: {
        newTab: 0,
        queryParamIndex: 0,
      },
    },
    offerLimitReached: { isOpen: false, props: undefined },
    ineligibleProductsSelected: { isOpen: false, props: undefined },
    archivePersonalization: { isOpen: false, props: undefined },
    unarchivePersonalization: { isOpen: false, props: undefined },
    relatedExperienceActive: { isOpen: false, props: undefined },
    relatedPersonalizationActive: { isOpen: false, props: undefined },
    copyPersonalization: { isOpen: false, props: undefined },
    archiveCustomEvent: { isOpen: false, props: undefined },
    unarchiveCustomEvent: { isOpen: false, props: undefined },
    saveWidgetConfirm: { isOpen: false, props: undefined },
    removeArchivedKeyMetric: { isOpen: false, props: undefined },
    offers: { isOpen: false, props: undefined },
    addOfferModifications: { isOpen: false, props: undefined },
    deleteComponent: { isOpen: false, props: undefined },
    removeComponent: { isOpen: false, props: undefined },
    quantityButtonsFullPageEditor: {
      isOpen: false,
      props: undefined,
    },
    messageBoxFullPageEditor: {
      isOpen: false,
      props: undefined,
    },
    offerProgressBarFullPageEditor: {
      isOpen: false,
      props: undefined,
    },
    shippingProgressBarFullPageEditor: {
      isOpen: false,
      props: undefined,
    },
  },
  timings: {
    initialPath: "",
    timings: {},
  },
  shareSettings: {
    showChange: true,
    showImpact: true,
    showName: true,
  },
};

const progressTypeMap: Record<ShopifyJobType, string> = {
  productProgress: "Saving Products",
  removeProductsFromCollection: "Removing Duplicates From Collections",
};

const getProgressName = (
  experimentName: string,
  progressType: ShopifyJobType,
  currentCount?: number,
  totalCount?: number
) => {
  const text = `${progressTypeMap[progressType]} (${experimentName})`;
  if (currentCount && totalCount) {
    return `${text} (${currentCount}/${totalCount})`;
  }
  return text;
};

const slice = createSlice({
  name: "global",
  initialState: initialGlobalState,
  reducers: {
    setIgnoreUpdateDetected: (global, value: PayloadAction<boolean>) => {
      global.ignoreUpdateDetected = value.payload;
    },
    setBypassUnsavedChanges: (global, value: PayloadAction<boolean>) => {
      global.bypassUnsavedChanges = value.payload;
    },
    setConfetti: (global, value: PayloadAction<boolean>) => {
      global.confetti = value.payload;
    },
    clearGlobal: (global) => {
      Object.assign(global, initialGlobalState);
    },
    setInitialTimingPath: (global, value: PayloadAction<string>) => {
      global.timings.initialPath = value.payload;
    },
    setTiming: (
      global,
      value: PayloadAction<{ key: string; value: number }>
    ) => {
      global.timings.timings[value.payload.key] = value.payload.value;
    },
    setEventProgress: (
      global,
      value: PayloadAction<Omit<WebsocketEventProgress, "type">>
    ) => {
      try {
        if (!(value.payload.experienceId in global.progresses)) {
          // @ts-ignore
          global.progresses[value.payload.experienceId] = {};
        }

        const newPercent = Math.max(
          global.progresses[value.payload.experienceId][value.payload.event]
            ?.progress || 0,
          value.payload.progress
        );

        if (
          !global.progresses[value.payload.experienceId][value.payload.event]
        ) {
          global.progresses[value.payload.experienceId][value.payload.event] = {
            progress: 0,
          };
        }

        global.progresses[value.payload.experienceId][
          value.payload.event
        ].progress = newPercent;

        if (
          global.progresses[value.payload.experienceId][value.payload.event]
            ?.toast
        ) {
          if (newPercent <= 1) {
            if (value.payload.event !== "productProgress") {
              toast.update(
                global.progresses[value.payload.experienceId][
                  value.payload.event
                ].toast!,
                {
                  render: getProgressName(
                    value.payload.experienceName,
                    value.payload.event,
                    value.payload.currentCount,
                    value.payload.totalCount
                  ),
                  progress: newPercent,
                }
              );
            }
          }
        } else if (value.payload.event !== "productProgress") {
          global.progresses[value.payload.experienceId][value.payload.event] = {
            toast: toast.info(
              getProgressName(
                value.payload.experienceName,
                value.payload.event,
                value.payload.currentCount,
                value.payload.totalCount
              ),
              {
                progress: newPercent,
              }
            ),
            progress: newPercent,
          };
        }
        if (newPercent >= 1) {
          try {
            toast.dismiss(
              global.progresses[value.payload.experienceId][value.payload.event]
                .toast
            );
          } catch (e) {
            // Do nothing
          }
          global.progresses[value.payload.experienceId][
            value.payload.event
          ].progress = 0;
        }
      } catch (e) {
        console.log(e);
        // Do nothing
      }
    },
    setDialog: <K extends keyof DialogProps>(
      global: IGlobalState,
      value: PayloadAction<{
        dialog: K;
        open: boolean;
        props?: DialogProps[K];
        clearProps?: boolean;
      }>
    ) => {
      global.dialogs[value.payload.dialog]!.isOpen = value.payload.open;
      if (value.payload.props) {
        global.dialogs[value.payload.dialog]!.props = value.payload.props;
      }
      if (value.payload.clearProps) {
        global.dialogs[value.payload.dialog]!.props = undefined;
      }
    },
    setShareImpact: (draft, action: PayloadAction<boolean>) => {
      draft.shareSettings.showImpact = action.payload;
    },
    setShareChange: (draft, action: PayloadAction<boolean>) => {
      draft.shareSettings.showChange = action.payload;
    },
    setShareName: (draft, action: PayloadAction<boolean>) => {
      draft.shareSettings.showName = action.payload;
    },
    setHasBottomAlert: (draft, action: PayloadAction<boolean>) => {
      draft.bottomAlert = action.payload;
    },
    setAiChat: <T extends keyof AiChat>(
      draft,
      action: PayloadAction<
        { key: T; value: AiChat[T] } | { key: T; value: AiChat[T] }[]
      >
    ) => {
      if (Array.isArray(action.payload)) {
        action.payload.forEach(({ key, value }) => {
          draft.aiChat[key] = value;
        });
        return;
      }

      draft.aiChat[action.payload.key] = action.payload.value;
    },
  },
});

export const {
  setIgnoreUpdateDetected,
  setBypassUnsavedChanges,
  setConfetti,
  setDialog,
  clearGlobal,
  setEventProgress,
  setTiming,
  setInitialTimingPath,
  setShareImpact,
  setShareChange,
  setShareName,
  setHasBottomAlert,
  setAiChat,
} = slice.actions;

export const isDialogOpen =
  (dialog: keyof DialogProps) => (state: RootState) => {
    return state.global.dialogs[dialog]!.isOpen;
  };

export const getDialogPropDetails =
  (dialog: keyof DialogProps) => (state: RootState) => {
    return state.global.dialogs[dialog]?.props;
  };

export const getDialog =
  <K extends keyof DialogProps>(dialog: K) =>
  (state: RootState): IGlobalState["dialogs"][K] => {
    return state.global.dialogs[dialog]!;
  };

export const getIgnoreUpdateDetected = (state: RootState) => {
  return state.global.ignoreUpdateDetected;
};

export const getBypassUnsavedChanges = (state: RootState) => {
  return state.global.bypassUnsavedChanges;
};

export const getProgresses = () => (state: RootState) => {
  return state.global.progresses;
};

export const getTimings = () => (state: RootState) => state.global.timings;

export const getShareSettings = () => (state: RootState) =>
  state.global.shareSettings;

export const getAiChatProps = (state: RootState) =>
  state.global.aiChat as RootState["global"]["aiChat"] & {
    initialContext: AiNodeContext | undefined;
  };

export const getIsAiChatOpen = (state: RootState) => {
  return state.global.aiChat.open && !!state.global.aiChat.workflowId;
};

export const globalReducer = slice.reducer;
